import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import { Box, FormControl, Typography, IconButton, Button, Autocomplete, TextField } from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseIcon from '@mui/icons-material/Close';

import Modal from 'components/common/modal';
import classes from './styles';
import M from 'messages';

/**
 * Turn API validation payload into plain text (sheet → row → messages).
 * Shape: { [sheetName]: { [rowNumber]: [{ message, column? }] } }
 */
const formatImportErrorsAsPlainText = errors => {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return 'No error details.';
  }
  const lines = ['Wine import — validation errors', '', 'Fix the issues below in your spreadsheet and try again.', ''];
  const sheetNames = Object.keys(errors);
  let any = false;
  for (const sheetName of sheetNames) {
    const sheetErrors = errors[sheetName];
    if (!sheetErrors || typeof sheetErrors !== 'object' || Array.isArray(sheetErrors)) continue;
    const rowKeys = Object.keys(sheetErrors).sort((a, b) => Number(a) - Number(b));
    if (!rowKeys.length) continue;
    any = true;
    lines.push(sheetName);
    lines.push('—'.repeat(Math.min(Math.max(sheetName.length, 8), 50)));
    for (const rowKey of rowKeys) {
      const rowErrors = sheetErrors[rowKey];
      if (!Array.isArray(rowErrors) || !rowErrors.length) continue;
      lines.push(`Row ${rowKey}`);
      for (const err of rowErrors) {
        const msg = typeof err === 'string' ? err : (err?.message ?? String(err));
        const column = typeof err === 'object' && err?.column ? String(err.column) : '';
        lines.push(column ? `  • ${column}: ${msg}` : `  • ${msg}`);
      }
      lines.push('');
    }
  }
  if (!any) {
    return 'No error details.';
  }
  return `${lines.join('\n').trimEnd()}\n`;
};

const ImportModal = ({ isOpen, partnersList, partnersLoading, handleClose, handleImport, importErrors }) => {
  const [importFile, setImportFile] = useState(null);
  const [importPartnerId, setImportPartnerId] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const onDrop = useCallback(file => {
    setImportFile(file?.[0]);
  }, []);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFilePicker,
  } = useDropzone({
    onDrop,
    noClick: true,
    multiple: true,
    disabled: importLoading,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
  });

  const handleCloseModal = () => {
    setImportFile(null);
    setImportPartnerId('');
    setImportLoading(false);
    handleClose();
  };

  const handleSubmit = async () => {
    if (importLoading) return;
    setImportLoading(true);
    const result = await handleImport(importFile, importPartnerId);
    if (result?.success) {
      handleCloseModal();
    }
    setImportLoading(false);
  };

  return (
    <Modal
      open={isOpen}
      leftBtnText={M.get('actions.cancel')}
      rightBtnText={M.get('actions.import')}
      handleClose={handleCloseModal}
      handleSubmit={handleSubmit}
      title={M.get('actions.import')}
      submitLoading={importLoading}
      disableSave={importLoading || !importFile || !importPartnerId}
      disableLeftBtn={importLoading}>
      <Box sx={classes.importModalContainer}>
        <FormControl fullWidth>
          <Autocomplete
            options={partnersList}
            getOptionLabel={option => option.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            required
            disabled={partnersLoading || importLoading}
            onChange={(event, newValue) => {
              setImportPartnerId(newValue ? newValue.id : null);
            }}
            renderInput={params => <TextField {...params} label={M.get('menuItem.filter.partner')} />}
          />
        </FormControl>
        <Box {...getRootProps()} sx={{ ...classes.dropzone, ...(importLoading ? classes.dropzoneDisabled : {}) }}>
          <input {...getInputProps()} />
          <FileUploadOutlinedIcon sx={classes.dropzoneIcon} />

          <Typography sx={classes.dropzoneText}>
            {isDragActive ? (
              M.get('wines.importModal.dropzone.dragActiveText')
            ) : (
              <>
                {M.get('wines.importModal.dropzone.uploadText')}{' '}
                <Box
                  component="button"
                  type="button"
                  disabled={importLoading}
                  onClick={event => {
                    event.stopPropagation();
                    openFilePicker();
                  }}
                  sx={{ ...classes.browseButton, ...(importLoading ? classes.browseButtonDisabled : {}) }}>
                  {M.get('wines.importModal.dropzone.browseLinkText')}
                </Box>
              </>
            )}
          </Typography>

          <Typography sx={classes.dropzoneMeta}>{M.get('wines.importModal.dropzone.acceptedFormatsText')}</Typography>
        </Box>
        {importFile && (
          <Box sx={classes.selectedFile}>
            <InsertDriveFileOutlinedIcon sx={classes.selectedFileIcon} />
            <Typography sx={classes.selectedFileName}>{importFile.name}</Typography>
            <IconButton size="small" disabled={importLoading} onClick={() => setImportFile(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        {!!importErrors && (
          <Box sx={classes.errorsContainer}>
            {/* Error text */}
            <Typography sx={classes.errorsText}>{M.get('wines.importModal.errors.text')}</Typography>
            <Typography sx={classes.errorsSubtext}>{M.get('wines.importModal.errors.subtext')}</Typography>

            {/* Download button */}
            <Button
              onClick={() => {
                const text = formatImportErrorsAsPlainText(importErrors);
                const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'import-errors.txt';
                a.rel = 'noopener';
                a.click();
                URL.revokeObjectURL(url);
              }}
              sx={classes.downloadButton}>
              {M.get('wines.importModal.errors.button')}
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

ImportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleImport: PropTypes.func.isRequired,
  partnersList: PropTypes.array.isRequired,
  partnersLoading: PropTypes.bool.isRequired,
  importErrors: PropTypes.object,
};

export default ImportModal;
